import React, { useCallback, useState } from 'react';
import { reportsAPI } from '../../services/api';
import { formatClinicDate } from '../../utils/clinicTime';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { fmtMoney, fmtNum } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Bills raised on one day and not closed on it: finished on a later day, or not finished at all.
// At the counter a bill is normally raised and paid in the same breath, so anything listed here was
// interrupted - the patient went to fetch money, a medicine was short, the shift ended. A bill still
// open counts its days up to today, so the longer it sits the worse it reads.

const NOT_COMPLETED = 'Not completed';

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #' },
  {
    key: 'invoiceDate',
    label: 'Raised',
    render: (r) => formatClinicDate(r.invoiceDate),
    csv: (r) => r.invoiceDate,
  },
  {
    key: 'paidAt',
    label: 'Completed',
    render: (r) => (r.paidAt
      ? formatClinicDate(r.paidAt)
      : <span className="font-semibold text-rose-600">{NOT_COMPLETED}</span>),
    csv: (r) => r.paidAt || NOT_COMPLETED,
  },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'phone', label: 'Phone' },
  {
    key: 'grandTotal',
    label: 'Total',
    align: 'right',
    render: (r) => fmtMoney(r.grandTotal),
    csv: (r) => r.grandTotal,
  },
  { key: 'status', label: 'Status' },
  { key: 'daysToComplete', label: 'Days', align: 'right' },
];

const NotCompletedSameDayReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getNotCompletedSameDay({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Bills', value: fmtNum(k.bills ?? 0) },
    { label: 'Completed Later', value: fmtNum(k.completedLater ?? 0) },
    {
      label: 'Still Open',
      value: fmtNum(k.stillOpen ?? 0),
      tone: Number(k.stillOpen) > 0 ? 'alert' : undefined,
    },
    { label: 'Value Still Open', value: fmtMoney(k.stillOpenValue ?? 0) },
    { label: 'Longest Gap', value: `${k.worstGapDays ?? 0} days` },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter
            value={range}
            preset={preset}
            onChange={(r, p) => { setRange(r); setPreset(p); }}
          />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <p className="text-sm text-gray-500">
        Bills raised in this period that were not completed on the day they were raised. The date
        range is the day a bill was raised, not the day it was completed.
      </p>

      <KpiStrip items={kpis} loading={loading} />

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'invoice', value: r.invoiceNumber })}
        title="Bills not completed the same day"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`not-completed-same-day-${range.from}-to-${range.to}`}
        emptyText="Every bill raised in this period was completed on the day it was raised."
      />

      <ReportPrintLayout
        title="Not Completed Same Day"
        subtitle={`Raised between ${range.from} and ${range.to}`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Bills not completed the same day', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default NotCompletedSameDayReport;
